
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { SeekJobCategorySuggest } from './job-categories';
import { SeekJobCategorySelect } from './job-categories';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "job-categories"
    }}>{`Job categories`}</h1>
    <p>{`Job categories identify the occupational category of a position.
Accurately categorising positions helps candidates search for the most relevant jobs.
They are also used as an input into `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/ad-selection"
      }}>{`SEEK’s ad pricing`}</a>{`.`}</p>
    <p>{`Job categories are represented as a two-level hierarchy from a general parent category to more specific child categories.
For example, this is a small subset of the hierarchy for Information & Communication Technology:`}</p>
    <img alt="" data-scoobie-style="none" src={require('../../../../mermaid/.3a040c747e64258fdc528c6fd682cc2f2be89bfd.mmd.svg')} title="" />
    <h2 {...{
      "id": "forward-compatibility"
    }}>{`Forward compatibility`}</h2>
    <p>{`SEEK periodically revises its job categories in response to changes in the employment market.
While completely removing a job category would be considered a breaking change, other revisions can be made without notice:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`New job categories can be added, both at the top level and as children of existing top-level job categories.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Existing job categories can be renamed to better reflect their intended purpose.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Deprecated job categories can be hidden from the `}<a parentName="p" {...{
            "href": "#jobcategorysuggestions"
          }}><inlineCode parentName="a">{`jobCategorySuggestions`}</inlineCode></a>{` and `}<a parentName="p" {...{
            "href": "#jobcategories"
          }}><inlineCode parentName="a">{`jobCategories`}</inlineCode></a>{` queries.`}</p>
      </li>
    </ul>
    <p>{`We recommend dynamically querying job categories as part of your software’s job posting flow.
This ensures hirers will be able to select from the latest SEEK job categories.`}</p>
    <h2 {...{
      "id": "before-you-begin"
    }}>{`Before you begin`}</h2>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`SEEK recommends using a `}<a parentName="p" {...{
            "href": "/auth/browser-tokens"
          }}>{`browser token`}</a>{` to query job categories directly from a hirer’s browser.
For the browser token to work, you will need to include the `}<inlineCode parentName="p">{`query:ontologies`}</inlineCode>{` scope in your request.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`You can also query job categories from your backend using a `}<a parentName="p" {...{
            "href": "/auth/partner-tokens"
          }}>{`partner token`}</a>{`.`}</p>
      </li>
    </ul>
    <h2 {...{
      "id": "implementation-options"
    }}>{`Implementation options`}</h2>
    <p>{`Job categories can be implemented in one of two ways.
These are listed in order of preference.`}</p>
    <h3 {...{
      "id": "option-1-support-dynamic-suggestions"
    }}>{`Option 1: Support dynamic suggestions`}</h3>
    <p>{`You can dynamically retrieve and display job category suggestions in your job posting flow.
This makes it easy for hirers to identify and select the most relevant job category for their position.`}</p>

    <SeekJobCategorySuggest mdxType="SeekJobCategorySuggest" />
    <p>{`Relevant job categories can be retrieved from the `}<a parentName="p" {...{
        "href": "#jobcategorysuggestions"
      }}><inlineCode parentName="a">{`jobCategorySuggestions`}</inlineCode></a>{` query.
The details of the position should be supplied to the query to tailor suggestions.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`Other`}</inlineCode>{` fallback must be implemented to allow hirers to manually select a job category where the suggestions do not suffice.
This is further documented as Option 2 below.`}</p>
    <h3 {...{
      "id": "option-2-provide-dropdown-fields"
    }}>{`Option 2: Provide dropdown fields`}</h3>
    <p>{`If your software cannot dynamically retrieve suggestions or the suggestions lacked the desired option,
you should allow hirers to manually select a job category from a set of dropdown fields.`}</p>

    <SeekJobCategorySelect mdxType="SeekJobCategorySelect" />
    <p>{`The top-level job categories and associated child categories can be retrieved from the `}<a parentName="p" {...{
        "href": "#jobcategories"
      }}><inlineCode parentName="a">{`jobCategories`}</inlineCode></a>{` query.`}</p>
    <p>{`We recommend running this query when loading your job posting flow to ensure that you surface the latest job categories.
You may use this method to cache job categories upfront,
but the cache should be periodically refreshed to handle changes to the SEEK job category hierarchy.
See `}<a parentName="p" {...{
        "href": "#forward-compatibility"
      }}>{`forward compatibility`}</a>{` for more details.`}</p>
    <h2 {...{
      "id": "operations"
    }}>{`Operations`}</h2>
    <p>{`The SEEK API provides three queries for looking up job categories:`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li"><a parentName="p" {...{
            "href": "#jobcategorysuggestions"
          }}><inlineCode parentName="a">{`jobCategorySuggestions`}</inlineCode></a>{` will suggest job categories based on a partial `}<a parentName="p" {...{
            "href": "https://developer.seek.com/schema/#/named-type/PositionProfile"
          }}><inlineCode parentName="a">{`PositionProfile`}</inlineCode></a>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li"><a parentName="p" {...{
            "href": "#jobcategories"
          }}><inlineCode parentName="a">{`jobCategories`}</inlineCode></a>{` will return the top-level categories for a scheme.
You can use this as a starting point for selecting a specific child category.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li"><a parentName="p" {...{
            "href": "#jobcategory"
          }}><inlineCode parentName="a">{`jobCategory`}</inlineCode></a>{` returns the job category for a given ID.`}</p>
      </li>
    </ol>
    <h3 {...{
      "id": "jobcategorysuggestions"
    }}>{`jobCategorySuggestions`}</h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/jobCategorySuggestions"
      }}><inlineCode parentName="a">{`jobCategorySuggestions`}</inlineCode>{` query`}</a>{` returns an array of suggested job categories based on a partial `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/PositionProfile"
      }}><inlineCode parentName="a">{`PositionProfile`}</inlineCode></a>{`.`}</p>
    <p>{`While only the position’s title is required, providing more fields will increase the relevance of the suggestions.
This can be used to interactively suggest job categories to a hirer while they’re posting a job ad.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query (\\n  $positionProfile: JobCategorySuggestionPositionProfileInput!\\n  $schemeId: String!\\n  $first: Int\\n) {\\n  jobCategorySuggestions(\\n    positionProfile: $positionProfile\\n    schemeId: $schemeId\\n    first: $first\\n  ) {\\n    jobCategory {\\n      id {\\n        value\\n      }\\n      name\\n      parent {\\n        id {\\n          value\\n        }\\n        name\\n      }\\n    }\\n    confidence\\n  }\\n}\",\"position\":{\"start\":{\"line\":97,\"column\":1,\"offset\":4146},\"end\":{\"line\":123,\"column\":4,\"offset\":4546},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"schemeId\\\": \\\"seekAnzPublicTest\\\",\\n  \\\"positionProfile\\\": {\\n    \\\"positionTitle\\\": \\\"Plumber needed\\\",\\n    \\\"positionLocation\\\": \\\"seekAnzPublicTest:location:seek:2bDJcFM6F\\\",\\n    \\\"positionOrganizations\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n    \\\"positionFormattedDescriptions\\\": [\\n      {\\n        \\\"descriptionId\\\": \\\"AdvertisementDetails\\\",\\n        \\\"content\\\": \\\"Are you the right fit to do <b>plumbing job</b>? Contact us if you are!\\\"\\n      }\\n    ]\\n  }\\n}\",\"position\":{\"start\":{\"line\":125,\"column\":1,\"offset\":4548},\"end\":{\"line\":140,\"column\":4,\"offset\":5013},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"jobCategorySuggestions\\\": [\\n    {\\n      \\\"jobCategory\\\": {\\n        \\\"id\\\": {\\n          \\\"value\\\": \\\"seekAnzPublicTest:jobCategory:seek:prQjC97D\\\"\\n        },\\n        \\\"name\\\": \\\"Plumbers\\\",\\n        \\\"parent\\\": {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"seekAnzPublicTest:jobCategory:seek:2j6o6hBgs\\\"\\n          },\\n          \\\"name\\\": \\\"Trades & Services\\\"\\n        }\\n      },\\n      \\\"confidence\\\": 1\\n    }\\n  ]\\n}\",\"position\":{\"start\":{\"line\":142,\"column\":1,\"offset\":5015},\"end\":{\"line\":162,\"column\":4,\"offset\":5417},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
        "(\\n": true,
        "": true,
        "$positionProfile:": true,
        "JobCategorySuggestionPositionProfileInput!\\n": true,
        "$schemeId:": true,
        "String!\\n": true,
        "$first:": true,
        "Int\\n)": true,
        "{\\n": true,
        "jobCategorySuggestions(\\n": true,
        "positionProfile:": true,
        "$positionProfile\\n": true,
        "schemeId:": true,
        "$schemeId\\n": true,
        "first:": true,
        "$first\\n": true,
        ")": true,
        "jobCategory": true,
        "id": true,
        "value\\n": true,
        "}\\n": true,
        "name\\n": true,
        "parent": true,
        "confidence\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":97,\"column\":1,\"offset\":4146},\"end\":{\"line\":123,\"column\":4,\"offset\":4546},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"schemeId\\\":": true,
        "\\\"seekAnzPublicTest\\\",\\n": true,
        "\\\"positionProfile\\\":": true,
        "\\\"positionTitle\\\":": true,
        "\\\"Plumber": true,
        "needed\\\",\\n": true,
        "\\\"positionLocation\\\":": true,
        "\\\"seekAnzPublicTest:location:seek:2bDJcFM6F\\\",\\n": true,
        "\\\"positionOrganizations\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n": true,
        "\\\"positionFormattedDescriptions\\\":": true,
        "[\\n": true,
        "\\\"descriptionId\\\":": true,
        "\\\"AdvertisementDetails\\\",\\n": true,
        "\\\"content\\\":": true,
        "\\\"Are": true,
        "you": true,
        "the": true,
        "right": true,
        "fit": true,
        "to": true,
        "do": true,
        "<b>plumbing": true,
        "job</b>?": true,
        "Contact": true,
        "us": true,
        "if": true,
        "are!\\\"\\n": true,
        "]\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":125,\"column\":1,\"offset\":4548},\"end\":{\"line\":140,\"column\":4,\"offset\":5013},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"jobCategorySuggestions\\\":": true,
        "\\\"jobCategory\\\":": true,
        "\\\"id\\\":": true,
        "\\\"value\\\":": true,
        "\\\"seekAnzPublicTest:jobCategory:seek:prQjC97D\\\"\\n": true,
        "},\\n": true,
        "\\\"name\\\":": true,
        "\\\"Plumbers\\\",\\n": true,
        "\\\"parent\\\":": true,
        "\\\"seekAnzPublicTest:jobCategory:seek:2j6o6hBgs\\\"\\n": true,
        "\\\"Trades": true,
        "&": true,
        "Services\\\"\\n": true,
        "\\\"confidence\\\":": true,
        "1\\n": true,
        "]\\n}\",\"position\":{\"start\":{\"line\":142,\"column\":1,\"offset\":5015},\"end\":{\"line\":162,\"column\":4,\"offset\":5417},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`query (
  $positionProfile: JobCategorySuggestionPositionProfileInput!
  $schemeId: String!
  $first: Int
) {
  jobCategorySuggestions(
    positionProfile: $positionProfile
    schemeId: $schemeId
    first: $first
  ) {
    jobCategory {
      id {
        value
      }
      name
      parent {
        id {
          value
        }
        name
      }
    }
    confidence
  }
}
`}</code></pre>
    <h3 {...{
      "id": "jobcategories"
    }}>{`jobCategories`}</h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/jobCategories"
      }}><inlineCode parentName="a">{`jobCategories`}</inlineCode>{` query`}</a>{` returns an array of top-level job categories based on a partial `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/PositionProfile"
      }}><inlineCode parentName="a">{`PositionProfile`}</inlineCode></a>{`.
These represent the most general job categories in the location’s hierarchy.`}</p>
    <p>{`You can use the top-level categories as a starting point for drilling down to a specific child category.
For example, you might present the hirer with a dropdown of top-level categories that populates a second dropdown with its children.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query (\\n  $schemeId: String!\\n  $positionProfile: JobCategories_PositionProfileInput!\\n) {\\n  jobCategories(schemeId: $schemeId, positionProfile: $positionProfile) {\\n    id {\\n      value\\n    }\\n    name\\n    children {\\n      id {\\n        value\\n      }\\n      name\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":175,\"column\":1,\"offset\":6052},\"end\":{\"line\":193,\"column\":4,\"offset\":6336},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"schemeId\\\": \\\"seekAnzPublicTest\\\",\\n  \\\"positionProfile\\\": {\\n    \\\"positionLocation\\\": \\\"seekAnzPublicTest:location:seek:2dkY4vZJF\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":195,\"column\":1,\"offset\":6338},\"end\":{\"line\":202,\"column\":4,\"offset\":6483},\"indent\":[1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"lovato-ignore-runnable-graphql-operation\",\"value\":\"{\\n  \\\"jobCategories\\\": [\\n    {\\n      \\\"id\\\": {\\n        \\\"value\\\": \\\"seekAnzPublicTest:jobCategory:seek:2WejkktVM\\\"\\n      },\\n      \\\"name\\\": \\\"Accounting\\\",\\n      \\\"children\\\": [\\n        {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"seekAnzPublicTest:jobCategory:seek:CTriSTrf\\\"\\n          },\\n          \\\"name\\\": \\\"Accounts Payable\\\"\\n        },\\n        {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"seekAnzPublicTest:jobCategory:seek:2cdSPDcy5\\\"\\n          },\\n          \\\"name\\\": \\\"Payroll\\\"\\n        },\\n        {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"seekAnzPublicTest:jobCategory:seek:3AXwHk3Km\\\"\\n          },\\n          \\\"name\\\": \\\"Accounts Officers/Clerks\\\"\\n        },\\n\\n        // ...\\n\\n        {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"seekAnzPublicTest:jobCategory:seek:91ppznsZ\\\"\\n          },\\n          \\\"name\\\": \\\"Taxation\\\"\\n        },\\n        {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"seekAnzPublicTest:jobCategory:seek:9Wi15HQB\\\"\\n          },\\n          \\\"name\\\": \\\"Treasury\\\"\\n        },\\n        {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"seekAnzPublicTest:jobCategory:seek:A1bB9mvo\\\"\\n          },\\n          \\\"name\\\": \\\"Other\\\"\\n        }\\n      ]\\n    },\\n\\n    // ...\\n\\n    {\\n      \\\"id\\\": {\\n        \\\"value\\\": \\\"seekAnzPublicTest:jobCategory:seek:vLTGe3Ls\\\"\\n      },\\n      \\\"name\\\": \\\"CEO & General Management\\\",\\n      \\\"children\\\": [\\n        {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"seekAnzPublicTest:jobCategory:seek:vqLSiXsV\\\"\\n          },\\n          \\\"name\\\": \\\"Board Appointments\\\"\\n        },\\n        {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"seekAnzPublicTest:jobCategory:seek:wLDco2Q7\\\"\\n          },\\n          \\\"name\\\": \\\"CEO\\\"\\n        },\\n        {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"seekAnzPublicTest:jobCategory:seek:wq6nsWvj\\\"\\n          },\\n          \\\"name\\\": \\\"COO & MD\\\"\\n        },\\n        {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"seekAnzPublicTest:jobCategory:seek:xKyxx1TM\\\"\\n          },\\n          \\\"name\\\": \\\"General/Business Unit Manager\\\"\\n        },\\n        {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"seekAnzPublicTest:jobCategory:seek:xps92Vyy\\\"\\n          },\\n          \\\"name\\\": \\\"Other\\\"\\n        }\\n      ]\\n    }\\n  ]\\n}\",\"position\":{\"start\":{\"line\":204,\"column\":1,\"offset\":6485},\"end\":{\"line\":297,\"column\":4,\"offset\":8592},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
        "(\\n": true,
        "": true,
        "$schemeId:": true,
        "String!\\n": true,
        "$positionProfile:": true,
        "JobCategories_PositionProfileInput!\\n)": true,
        "{\\n": true,
        "jobCategories(schemeId:": true,
        "$schemeId,": true,
        "positionProfile:": true,
        "$positionProfile)": true,
        "id": true,
        "value\\n": true,
        "}\\n": true,
        "name\\n": true,
        "children": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":175,\"column\":1,\"offset\":6052},\"end\":{\"line\":193,\"column\":4,\"offset\":6336},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"schemeId\\\":": true,
        "\\\"seekAnzPublicTest\\\",\\n": true,
        "\\\"positionProfile\\\":": true,
        "\\\"positionLocation\\\":": true,
        "\\\"seekAnzPublicTest:location:seek:2dkY4vZJF\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":195,\"column\":1,\"offset\":6338},\"end\":{\"line\":202,\"column\":4,\"offset\":6483},\"indent\":[1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"lovato-ignore-runnable-graphql-operation\",\"value\":\"{\\n": true,
        "\\\"jobCategories\\\":": true,
        "[\\n": true,
        "\\\"id\\\":": true,
        "\\\"value\\\":": true,
        "\\\"seekAnzPublicTest:jobCategory:seek:2WejkktVM\\\"\\n": true,
        "},\\n": true,
        "\\\"name\\\":": true,
        "\\\"Accounting\\\",\\n": true,
        "\\\"children\\\":": true,
        "\\\"seekAnzPublicTest:jobCategory:seek:CTriSTrf\\\"\\n": true,
        "\\\"Accounts": true,
        "Payable\\\"\\n": true,
        "\\\"seekAnzPublicTest:jobCategory:seek:2cdSPDcy5\\\"\\n": true,
        "\\\"Payroll\\\"\\n": true,
        "\\\"seekAnzPublicTest:jobCategory:seek:3AXwHk3Km\\\"\\n": true,
        "Officers/Clerks\\\"\\n": true,
        "},\\n\\n": true,
        "//": true,
        "...\\n\\n": true,
        "\\\"seekAnzPublicTest:jobCategory:seek:91ppznsZ\\\"\\n": true,
        "\\\"Taxation\\\"\\n": true,
        "\\\"seekAnzPublicTest:jobCategory:seek:9Wi15HQB\\\"\\n": true,
        "\\\"Treasury\\\"\\n": true,
        "\\\"seekAnzPublicTest:jobCategory:seek:A1bB9mvo\\\"\\n": true,
        "\\\"Other\\\"\\n": true,
        "]\\n": true,
        "\\\"seekAnzPublicTest:jobCategory:seek:vLTGe3Ls\\\"\\n": true,
        "\\\"CEO": true,
        "&": true,
        "General": true,
        "Management\\\",\\n": true,
        "\\\"seekAnzPublicTest:jobCategory:seek:vqLSiXsV\\\"\\n": true,
        "\\\"Board": true,
        "Appointments\\\"\\n": true,
        "\\\"seekAnzPublicTest:jobCategory:seek:wLDco2Q7\\\"\\n": true,
        "\\\"CEO\\\"\\n": true,
        "\\\"seekAnzPublicTest:jobCategory:seek:wq6nsWvj\\\"\\n": true,
        "\\\"COO": true,
        "MD\\\"\\n": true,
        "\\\"seekAnzPublicTest:jobCategory:seek:xKyxx1TM\\\"\\n": true,
        "\\\"General/Business": true,
        "Unit": true,
        "Manager\\\"\\n": true,
        "\\\"seekAnzPublicTest:jobCategory:seek:xps92Vyy\\\"\\n": true,
        "]\\n}\",\"position\":{\"start\":{\"line\":204,\"column\":1,\"offset\":6485},\"end\":{\"line\":297,\"column\":4,\"offset\":8592},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`query (
  $schemeId: String!
  $positionProfile: JobCategories_PositionProfileInput!
) {
  jobCategories(schemeId: $schemeId, positionProfile: $positionProfile) {
    id {
      value
    }
    name
    children {
      id {
        value
      }
      name
    }
  }
}
`}</code></pre>
    <h3 {...{
      "id": "jobcategory"
    }}>{`jobCategory`}</h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/jobCategory"
      }}><inlineCode parentName="a">{`jobCategory`}</inlineCode>{` query`}</a>{` retrieves a job category by ID.
This can be useful for debugging or exploratory testing:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query ($id: String!) {\\n  jobCategory(id: $id) {\\n    id {\\n      value\\n    }\\n    parent {\\n      id {\\n        value\\n      }\\n      name\\n    }\\n    name\\n    children {\\n      id {\\n        value\\n      }\\n      name\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":306,\"column\":1,\"offset\":8809},\"end\":{\"line\":327,\"column\":4,\"offset\":9041},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"id\\\": \\\"seekAnzPublicTest:jobCategory:seek:xKyxx1TM\\\"\\n}\",\"position\":{\"start\":{\"line\":329,\"column\":1,\"offset\":9043},\"end\":{\"line\":333,\"column\":4,\"offset\":9112},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"jobCategory\\\": {\\n    \\\"id\\\": {\\n      \\\"value\\\": \\\"seekAnzPublicTest:jobCategory:seek:xKyxx1TM\\\"\\n    },\\n    \\\"parent\\\": {\\n      \\\"id\\\": {\\n        \\\"value\\\": \\\"seekAnzPublicTest:jobCategory:seek:vLTGe3Ls\\\"\\n      },\\n      \\\"name\\\": \\\"CEO & General Management\\\"\\n    },\\n    \\\"name\\\": \\\"General/Business Unit Manager\\\",\\n    \\\"children\\\": null\\n  }\\n}\",\"position\":{\"start\":{\"line\":335,\"column\":1,\"offset\":9114},\"end\":{\"line\":351,\"column\":4,\"offset\":9448},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
        "($id:": true,
        "String!)": true,
        "{\\n": true,
        "": true,
        "jobCategory(id:": true,
        "$id)": true,
        "id": true,
        "value\\n": true,
        "}\\n": true,
        "parent": true,
        "name\\n": true,
        "children": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":306,\"column\":1,\"offset\":8809},\"end\":{\"line\":327,\"column\":4,\"offset\":9041},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"id\\\":": true,
        "\\\"seekAnzPublicTest:jobCategory:seek:xKyxx1TM\\\"\\n}\",\"position\":{\"start\":{\"line\":329,\"column\":1,\"offset\":9043},\"end\":{\"line\":333,\"column\":4,\"offset\":9112},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"jobCategory\\\":": true,
        "\\\"value\\\":": true,
        "\\\"seekAnzPublicTest:jobCategory:seek:xKyxx1TM\\\"\\n": true,
        "},\\n": true,
        "\\\"parent\\\":": true,
        "\\\"seekAnzPublicTest:jobCategory:seek:vLTGe3Ls\\\"\\n": true,
        "\\\"name\\\":": true,
        "\\\"CEO": true,
        "&": true,
        "General": true,
        "Management\\\"\\n": true,
        "\\\"General/Business": true,
        "Unit": true,
        "Manager\\\",\\n": true,
        "\\\"children\\\":": true,
        "null\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":335,\"column\":1,\"offset\":9114},\"end\":{\"line\":351,\"column\":4,\"offset\":9448},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`query ($id: String!) {
  jobCategory(id: $id) {
    id {
      value
    }
    parent {
      id {
        value
      }
      name
    }
    name
    children {
      id {
        value
      }
      name
    }
  }
}
`}</code></pre>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;